import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import { initAnalytics, trackPageView } from './analytics';
import HomePage from './pages/HomePage';

// El resto de rutas va en chunks aparte para que el home cargue rápido
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const EventosPage = lazy(() => import('./pages/EventosPage'));
const EventTypePage = lazy(() => import('./pages/EventTypePage'));
const VentaPage = lazy(() => import('./pages/VentaPage'));
const PreciosPage = lazy(() => import('./pages/PreciosPage'));
const CiudadesPage = lazy(() => import('./pages/CiudadesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

const PageLoader: React.FC = () => (
  <div className="flex min-h-[70vh] items-center justify-center bg-cabina-cream">
    <div className="h-9 w-9 animate-spin rounded-full border-2 border-cabina-ink/10 border-t-cabina-red" />
  </div>
);

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    initAnalytics();
  }, []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);

  return null;
};

/**
 * Contenido de la app SIN router, para poder montarlo con dos routers
 * distintos: BrowserRouter en el navegador (main.tsx) y StaticRouter al
 * generar el HTML estático del build (entry-server.tsx). Si el router
 * estuviera aquí dentro, el prerender no podría fijar la ruta a renderizar.
 */
export const AppShell: React.FC = () => (
  <>
    <ScrollToTop />
    <div className="flex min-h-screen flex-col bg-cabina-cream font-sans text-cabina-ink">
      <Header />

      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />

            {/* Catálogo */}
            <Route path="/catalogo" element={<CatalogPage />} />
            <Route path="/catalogo/:slug" element={<ProductPage />} />

            {/* Tipos de evento */}
            <Route path="/eventos" element={<EventosPage />} />
            <Route path="/eventos/:slug" element={<EventTypePage />} />

            {/* Venta de equipos */}
            <Route path="/venta" element={<VentaPage />} />

            {/* URLs anteriores: se conservan como redirección para no perder
                enlaces ya compartidos. El 301 real lo hace vercel.json. */}
            <Route path="/bodas" element={<Navigate to="/eventos/bodas" replace />} />
            <Route
              path="/empresas"
              element={<Navigate to="/eventos/eventos-empresariales" replace />}
            />

            {/* Precios y cobertura */}
            <Route path="/precios" element={<PreciosPage />} />
            <Route path="/ciudades" element={<CiudadesPage />} />
            <Route path="/ciudades/:slug" element={<CiudadesPage />} />

            <Route path="/contacto" element={<ContactPage />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  </>
);

const App: React.FC = () => (
  <Router>
    <AppShell />
  </Router>
);

export default App;
